import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, AfterViewChecked, ElementRef, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { Router } from '@angular/router';
import { SubprojectsService } from 'src/app/akita/subproject/state/subprojects.service';
import { SharedProjectService } from 'src/app/core/service/api/shared-project.service';
import { translate } from "@jsverse/transloco";
import { showFeedbackSaved } from 'src/app/core/util/notification';
import { Globals } from 'src/app/globals';
import { NewEntityData } from 'src/app/shared/directive/new-entity.directive';
import { ProjectShareComponent } from '../project-share/project-share.component';
import { modalConfig, ModalEnum } from 'src/app/core/util/modalConfig';
import { forkJoin } from 'rxjs';

export interface PeriodicElement {
  id: number
  name: string;
  level: string;
  item: string;
  status: string;
  source_project_id: number;
  target_project_id: number;
  is_external_project?: boolean;
  shared_level: string;
  is_receiver?: boolean;
  metadata?: {
    level_upgraded?: boolean;
    modules_added?: Array<{id: number, module_id: string, module_name: string}>;
    users_added?: Array<{id: number, [key: string]: any}>;
    shared_status?: string;
  };
  old_shared_level?: string;
}

const ELEMENT_DATA: PeriodicElement[] = [
];

@Component({
  selector: 'app-connection-status-modal',
  templateUrl: './connection-status-modal.component.html',
  styleUrl: './connection-status-modal.component.sass',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConnectionStatusModalComponent implements AfterViewChecked {
  declare public entity: any;
  dataSource = ELEMENT_DATA;
  renderedData: any[] = [];
  isProcessing = false;
  displayedColumns: string[] = ['Project name', 'Collaboration', 'items to share', 'Action'];
  groupHeaderColumns: string[] = ['groupHeader'];
  expandedRowIds: Set<number> = new Set();
  @ViewChild('tableContainer', { static: false }) tableContainer!: ElementRef;
  private positioningDone = false;
  public selectedTabIndex: number = 0;

  constructor(private dialog: MatDialog, @Inject(MAT_DIALOG_DATA) public data: any, private router: Router, public dialogRef: MatDialogRef<ConnectionStatusModalComponent>, public globals: Globals, private cdRef: ChangeDetectorRef, private sharedProjectService: SharedProjectService, private _subprojectsService: SubprojectsService) {
  }

  ngOnInit() {
    if (!this.data?.item) {
      this.load('received');
      return;
    }

    const data = this.data.item?.data?.data;
    const type = data?.type ?? '';

    const isTarget = data?.metadata?.target_project_code === this.globals.projectCode;
    const isSource = data?.metadata?.source_project_code === this.globals.projectCode;

    const isRequested = type.includes('requested');
    const isAccepted = type.includes('accepted');
    const isCancelled = type.includes('cancelled');
    const isUpgrade = type.includes('upgrade');

    // TARGET SIDE
    if (isTarget) {
      if (isRequested) {
        this.openTab(0, 'received');
        return;
      }

      if (isCancelled) {
        this.openTab(2, 'cancelled');
        return;
      }
    }

    // SOURCE SIDE
    if (isSource) {
      // Sent + cancelled (non-upgrade)
      const shouldOpenCancelled = isCancelled && !isUpgrade;

      // Sent tab logic for accepted / upgrade
      const shouldOpenSent = (isUpgrade && (isAccepted || isCancelled)) || (isAccepted && !isUpgrade);

      if (shouldOpenCancelled) {
        this.openTab(2, 'cancelled');
        return;
      }

      if (shouldOpenSent) {
        this.openTab(1, 'sent');
        return;
      }
    }

    this.load('received');
  }

  private openTab(index: number, type: 'received' | 'sent' | 'cancelled') {
    this.selectedTabIndex = index;
    this.load(type);
  }

  onTabChange(event: MatTabChangeEvent) {
    if (event.index === 0) {
      this.load('received');
    } else if (event.index === 1) { // "Sent" tab
      this.load('sent');
    } else if (event.index === 2) {
      this.load('cancelled');
    }
  }

  load(type: string) {
    this.isProcessing = true;

    if (type!=='cancelled') {
      this.sharedProjectService.getSharedStatus(type).subscribe((response: any) => {
        this.dataSource = []; // Clear previous data
        this.expandedRowIds.clear();
        const seenIds = new Set<number>();
        response.forEach((request) => {
          if (request.status !== 'rejected' && request.status !== 'cancelled' && !seenIds.has(request.id)) {
            seenIds.add(request.id);
            const element = {
              id: request.id,
              name: request.side === 'target' ? request.source_project.name: request.target_project.name,
              level: request.shared_level,
              item: this.modulesShow(request),
              status: request.status,
              source_project_id: request.source_project_id,
              target_project_id: request.target_project_id,
              is_external_project: request.is_external_project,
              shared_level: request.shared_level,
              is_receiver: request.side === 'target',
              metadata: request.metadata
            };
            this.dataSource.push(element);
            // Expand row by default if it has metadata info
            if (this.hasMetadataInfo(element)) {
              this.expandedRowIds.add(element.id);
            }
          }
        })
        this.sortDataSourceByProjectType();
        this.isProcessing = false;
        this.positioningDone = false;
        this.cdRef.detectChanges();
        // Position detail boxes after view updates
        setTimeout(() => this.positionDetailBoxes(), 0);
      })
    } else {
      this.sharedProjectService.getCancelledSharedProjects().subscribe((resp) => {
        this.dataSource = []; // Clear previous data
        this.expandedRowIds.clear();
        resp.forEach((request) => {
          const element = {
            id: request.id,
            name: request.side === 'target' ? request.source_project.name: request.target_project.name,
            level: request.shared_level,
            item: request.modules.map(item => translate('reusable.project_setting.' + item.module_name)).join(", "),
            status: request.status,
            source_project_id: request.source_project_id,
            target_project_id: request.target_project_id,
            is_external_project: request.is_external_project,
            shared_level: request.shared_level,
            is_receiver: request.side === 'target',
              metadata: {
                level_upgraded: true,
                modules_added: [{id: 1, module_id: 'contactpersons', module_name: 'ContactPersons'}],
                users_added: [{id: 1, name: 'Nouman Khan', email: 'nouman@example.com'}],
              }
          };
          this.dataSource.push(element);
          // Expand row by default if it has metadata info
          if (this.hasMetadataInfo(element)) {
            this.expandedRowIds.add(element.id);
          }
        })

        this.sortDataSourceByProjectType();
        this.isProcessing = false;
        this.positioningDone = false;
        this.cdRef.detectChanges();
        // Position detail boxes after view updates
        setTimeout(() => this.positionDetailBoxes(), 0);
      });
    }
  }

  modulesShow(request: any): string {
    if (request.shared_level === 'read') {
      if (request.status === 'requested') {
        const allModules = [
          ...(request.modules || []),
          ...(request.metadata?.modules_added || [])
        ];
        
        let uniqueModules = allModules.filter((module, index, self) => 
          index === self.findIndex(m => (m.module_id) === (module.module_id))
        );
  
        if (request.metadata?.modules_removed && request.metadata?.modules_removed.length > 0) {
          const removedModuleIds = request.metadata.modules_removed.map((r: any) => r.module_id || r.id || r);
          uniqueModules = uniqueModules.filter(m => !removedModuleIds.includes(m.module_id || m.id));
        }
        
        return uniqueModules.map(m => translate('reusable.project_setting.' + m.module_name)).join(", ");
      } else {
        return request.modules.map(item => translate('reusable.project_setting.' + item.module_name)).join(", ");
      }
    } else {
      return '';
    }
  }

  ngAfterViewChecked() {
    if (!this.positioningDone && !this.isProcessing) {
      this.positionDetailBoxes();
    }
  }

  private positionDetailBoxes() {
    if (!this.tableContainer || this.isProcessing) {
      return;
    }

    const table = this.tableContainer.nativeElement.querySelector('table');
    if (!table) {
      return;
    }

    // Find the tbody element
    const tbody = table.querySelector('tbody');
    if (!tbody) {
      return;
    }

    // First, remove any existing detail rows that are no longer valid
    const existingDetailRows = Array.from(tbody.querySelectorAll('tr.detail-row-wrapper')) as HTMLElement[];
    existingDetailRows.forEach(detailRow => {
      const rowId = detailRow.previousElementSibling?.getAttribute('data-row-id');
      if (rowId) {
        const element = this.dataSource.find((e: PeriodicElement) => e.id.toString() === rowId);
        // Remove detail row if element doesn't exist, status is not 'requested' (or shared_status is not 'requested'), or not expanded
        if (!element || (element.status !== 'requested' && element.metadata?.shared_status !== 'requested') || !this.isRowExpanded(element) || !this.hasMetadataInfo(element)) {
          detailRow.remove();
        }
      }
    });

    // Find all data rows (skip header row)
    const rows = Array.from(tbody.querySelectorAll('tr[data-row-id]')) as HTMLElement[];
    
    // Find all detail boxes that should be displayed
    const detailBoxes = Array.from(this.tableContainer.nativeElement.querySelectorAll('.changes-detail-wrapper[data-detail-for]')) as HTMLElement[];

    detailBoxes.forEach(detailBox => {
      const rowId = detailBox.getAttribute('data-detail-for');
      if (!rowId) return;

      // Verify the element still exists and has the correct status
      const element = this.dataSource.find((e: PeriodicElement) => e.id.toString() === rowId);
      if (!element || (element.status !== 'requested' && element.metadata?.shared_status !== 'requested') || !this.isRowExpanded(element) || !this.hasMetadataInfo(element)) {
        detailBox.remove();
        return;
      }

      // Find the corresponding row
      const correspondingRow = rows.find(row => row.getAttribute('data-row-id') === rowId);
      if (correspondingRow && !correspondingRow.nextElementSibling?.classList.contains('detail-row-wrapper')) {
        // Create a detail row element
        const detailRow = document.createElement('tr');
        detailRow.className = 'detail-row-wrapper';
        detailRow.style.display = 'table-row';
        
        const detailCell = document.createElement('td');
        detailCell.setAttribute('colspan', '4');
        detailCell.className = 'detail-cell';
        detailCell.style.padding = '0';
        detailCell.style.border = 'none';
        detailCell.style.backgroundColor = 'transparent';
        
        // Move the detail box content into the cell
        while (detailBox.firstChild) {
          detailCell.appendChild(detailBox.firstChild);
        }
        detailRow.appendChild(detailCell);
        
        // Insert after the corresponding row
        correspondingRow.parentNode?.insertBefore(detailRow, correspondingRow.nextSibling);
        
        // Remove the original detail box wrapper
        detailBox.remove();
      }
    });

    this.positioningDone = true;
  }

  private sortDataSourceByProjectType(): void {
    this.dataSource = [...this.dataSource].sort((a, b) => {
      const aExt = !!a.is_external_project;
      const bExt = !!b.is_external_project;

      if (aExt === bExt) {
        return a.name.localeCompare(b.name);
      }

      return aExt ? 1 : -1; // internal (false) first, external (true) after
    });
    this.buildRenderedData();
  }

  private buildRenderedData(): void {
    const internal = this.dataSource.filter(e => !e.is_external_project);
    const external = this.dataSource.filter(e => e.is_external_project);

    const result: any[] = [];

    if (internal.length) {
      result.push({ isGroupHeader: true, label: 'internal' });
      result.push(...internal);
    }

    if (external.length) {
      result.push({ isGroupHeader: true, label: 'external' });
      result.push(...external);
    }

    this.renderedData = result;
  }

  changeStatus(element: any, status: string, fromDetailSection: boolean = false) {
    const data: any = {
      source_project_id: element.source_project_id,
      target_project_id: element.target_project_id,
      shared_level: element.shared_level,
      status: fromDetailSection ? element.status : status
    }

    // Add shared_status parameter when called from detail section
    if (fromDetailSection && (status === 'accepted' || status === 'cancelled')) {
      data.shared_status = status;
    }

    this.sharedProjectService.updateSharedProjectDetail(element.id, data).subscribe((response: any) => {
      // Remove item if rejected or requested
      // Don't remove if cancelled from detail section
      if (status === 'rejected' || status === 'requested' || (status === 'cancelled' && !fromDetailSection)) {
        this.dataSource = this.dataSource.filter(item => item.id !== element.id);
        // Remove from expanded rows
        this.expandedRowIds.delete(element.id);
      } else {
        // Update element with API response when status is accepted or cancelled
        if (status === 'accepted' || status === 'cancelled') {
          this.dataSource = this.dataSource.map(item => {
            if (item.id === element.id) {
              // Merge metadata properly - if response has metadata, use it, otherwise update existing metadata
              let updatedMetadata = item.metadata ? { ...item.metadata } : {};
              if (response.metadata) {
                updatedMetadata = { ...updatedMetadata, ...response.metadata };
              }

              // Clear shared_status when accepting - if response status is accepted, remove shared_status
              const finalStatus = response.status || status;
              if (finalStatus === 'accepted' && updatedMetadata.shared_status === 'requested') {
                delete updatedMetadata.shared_status;
              }

              // Update item field (modules) if response contains modules
              let updatedItem = item.item;
              if (response.modules && Array.isArray(response.modules) && response.modules.length > 0) {
                updatedItem = response.modules.map((module: any) => translate('reusable.project_setting.' + module.module_name)).join(", ");
              }

              // Update level if shared_level changed
              const updatedLevel = response.shared_level || item.shared_level;

              return {
                ...item,
                status: finalStatus,
                level: updatedLevel,
                shared_level: updatedLevel,
                item: updatedItem,
                metadata: updatedMetadata,
                // Update other fields from response if they exist
                ...(response.source_project_id && { source_project_id: response.source_project_id }),
                ...(response.target_project_id && { target_project_id: response.target_project_id })
              };
            }
            return item;
          });
        } else {
          // update status normally for other statuses
          this.dataSource = this.dataSource.map(item =>
            item.id === element.id ? { ...item, status } : item
          );
        }
        // Remove from expanded rows if status is accepted (no longer show details)
        if (status === 'accepted' || (fromDetailSection && status === 'cancelled')) {
          this.expandedRowIds.delete(element.id);
        }
      }

      this.sortDataSourceByProjectType();
      this.positioningDone = false;
      // Immediately remove detail boxes for accepted items
      if (status === 'accepted' || (fromDetailSection && status === 'cancelled')) {
        this.removeDetailBoxForElement(element.id);
      }
      this.cdRef.markForCheck();
      this.loadSharedProjectsIntoGlobal();
      if (response.shared_level==='read' && response.status==='accepted') {
        this.loadSubprojectsIntoGlobal();
      }
      this.cdRef.detectChanges();
      // Reposition detail boxes after status change
      setTimeout(() => {
        this.positionDetailBoxes();
        this.cdRef.detectChanges();
      }, 0);
      showFeedbackSaved();
    });
  }

  private removeDetailBoxForElement(elementId: number): void {
    if (!this.tableContainer) {
      return;
    }

    const table = this.tableContainer.nativeElement.querySelector('table');
    if (!table) {
      return;
    }

    const tbody = table.querySelector('tbody');
    if (!tbody) {
      return;
    }

    // Find and remove detail row for this element
    const rows = Array.from(tbody.querySelectorAll('tr[data-row-id]')) as HTMLElement[];
    const targetRow = rows.find(row => row.getAttribute('data-row-id') === elementId.toString());
    
    if (targetRow) {
      const nextSibling = targetRow.nextElementSibling;
      if (nextSibling && nextSibling.classList.contains('detail-row-wrapper')) {
        nextSibling.remove();
      }
    }

    // Also remove any detail boxes outside the table
    const detailBoxes = Array.from(this.tableContainer.nativeElement.querySelectorAll('.changes-detail-wrapper[data-detail-for="' + elementId + '"]')) as HTMLElement[];
    detailBoxes.forEach(box => box.remove());
  }

  private loadSharedProjectsIntoGlobal() {
    this.sharedProjectService.getSharedProjects().subscribe((projects)=>{
      this.globals.shared_projects = projects;
    })
  }

  public loadSubprojectsIntoGlobal(){
    this._subprojectsService.get(true).subscribe(res=>{
      this.globals.subprojects=res;
    })
  }

  navigateToKaart(){
    const url = `/${this.router.url.split("/")[1]}/kaart`;
    if (url) {
      this.dialogRef.close();
      this.router.navigateByUrl(url);
    }
  }

  protected initializeEntity(): void {}
  protected submit(): void {}

  resendConnectionRequest(item: any, status: 'requested') {
    this.changeStatus(item, status);
  }

  public editConnection(item: any) {
    const data = {
      name: item.name || 'Unnamed Project',
      project: {...item, id: item.target_project_id, shared_id: item.id},
    };
    
    this.dialogRef.close();
    this.openProjectShareModal(data);
  }

  private openProjectShareModal(data: any) {
    this.dialog.open(ProjectShareComponent,
      modalConfig(
        {
          data: {...data, type: 'customerProjects', openPreviousModal: true},
          disableClose: true,
        },
        ModalEnum.ModalMedium
      )
    )
    .afterClosed().subscribe((reOpenModal) => {
      if (reOpenModal) this.openConnectionStatusModal();
    });
  }

  private openConnectionStatusModal(){
    const modal = this.dialog.open(ConnectionStatusModalComponent, modalConfig({
      disableClose: true,
      closeOnNavigation: true
    }, ModalEnum.ModalMedium))
    modal.afterClosed().subscribe(() => {
      this.load('received');
    })
  }


  hasMetadataInfo(element: PeriodicElement): boolean {
    return !!(element.metadata && 
      (element.metadata.shared_status ||
       element.metadata.level_upgraded ||
       (element.metadata.modules_added && element.metadata.modules_added.length > 0) ||
       (element.metadata.users_added && element.metadata.users_added.length > 0)));
  }

  getChangesCount(element: PeriodicElement): number {
    if (!element.metadata) {
      return 0;
    }
    let count = 0;
  
    if (element.metadata.modules_added && element.metadata.modules_added.length > 0) {
      count += element.metadata.modules_added.length;
    }
    if (element.metadata.users_added && element.metadata.users_added.length > 0) {
      count += element.metadata.users_added.length;
    }
    return count;
  }

  getCollaborationLevelChange(element: PeriodicElement): { old: string, new: string } | null {
    if (!element.metadata || !element.metadata.level_upgraded) {
      return null;
    }
    // If level_upgraded is true, it means it went from chat (1) to read (2)
    // The current shared_level is the new level (read)
    // So old level is always 'chat' when level_upgraded is true
    return { old: 'chat_sidebar.chat', new: 'chat_sidebar.read' };
  }

  hasLevelUpgrade(element: PeriodicElement): boolean {
    return !!(element.metadata && element.metadata.level_upgraded);
  }

  isRowExpanded(element: PeriodicElement): boolean {
    return this.expandedRowIds.has(element.id);
  }

  trackByFn(index: number, item: PeriodicElement): any {
    return item ? item.id : index;
  }

  isGroupHeaderRow = (_index: number, row: any): boolean => !!row.isGroupHeader;

  isProjectRow = (_index: number, row: any): boolean => !row.isGroupHeader;

  getChangesList(element: PeriodicElement): { type: string; items: string[] }[] {
    if (!element.metadata) {
      return [];
    }
    const changes: { type: string; items: string[] }[] = [];
    
    if (element.metadata.modules_added && element.metadata.modules_added.length > 0) {
      const moduleNames = element.metadata.modules_added
        .map((m: any) => {
          try {
            return translate('reusable.project_setting.' + m.module_name);
          } catch {
            return m.module_name || m.module_id || `Module ${m.id}`;
          }
        });
      changes.push({ type: 'Modules', items: moduleNames });
    }
    
    if (element.metadata.users_added && element.metadata.users_added.length > 0) {
      const userNames = element.metadata.users_added
        .map((u: any) => {
          if (u.name) return u.name;
          if (u.email) return u.email;
          if (typeof u === 'object' && u.id) return `User ${u.id}`;
          return String(u);
        });
      changes.push({ type: 'Users', items: userNames });
    }
    
    return changes;
  }
}
